// saga/AuthSaga.js
import { call, put, takeLatest } from "redux-saga/effects";
import axios from "axios";
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  signupRequest,
  signupSuccess,
  signupFailure,
  getDesignationsRequest,
  getDesignationsSuccess,
  getDesignationsFailure,
  getUserDetailsRequest,
  getUserDetailsSuccess,
  getUserDetailsFailure,
  logoutRequest,
  logoutSuccess,
  logoutFailure,
} from "../reducer/AuthReducer";

import { resetArtworkShare } from "../reducer/ArtworkShareReducer";
import { resetArtwork } from "../reducer/ArtworkReducer";
import { resetArtworkView } from "../reducer/ArtworkViewReducer";

import { postApi, postApiV2 } from "../../utils/ApiRequest";

// ✅ LOGIN
export function* loginSaga(action) {
  try {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    const response = yield call(postApi, "auth/login", action.payload, headers);

    if (response?.status === 200) {
      yield put(loginSuccess(response.data));
      // store tokens in localStorage for later use
      localStorage.setItem("access_token", response.data?.data?.session?.access_token);
      localStorage.setItem("user_id", response.data?.data?.user_id);
    } else {
      yield put(loginFailure({ type: "login", message: "Invalid credentials" }));
    }
  } catch (error) {
    yield put(
      loginFailure({
        type: "login",
        message:
          error?.response?.data?.error?.message ||
          error?.message ||
          "Login failed",
      })
    );
  }
}

// ✅ SIGNUP
export function* signupSaga(action) {
  try {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    const response = yield call(postApiV2, "auth/register", action.payload, headers);

    if (response?.status === 200 || response?.status === 201) {
      const nextApi = yield call(axios.post, "https://jsonplaceholder.typicode.com/posts");
      console.log("Next API data:", nextApi.data);

      yield put(signupSuccess(response.data));
    } else {
      yield put(
        signupFailure({ type: "signup", message: "User already exists" })
      );
    }
  } catch (error) {
    yield put(
      signupFailure({
        type: "signup",
        message:
          error?.response?.data?.error?.message ||
          error?.message ||
          "Signup failed",
      })
    );
  }
}


// ✅ FETCH DESIGNATIONS
export function* getDesignationsSaga(action) {
  try {
    const deptId = action.payload;

    // you can use fetch or axios — keeping fetch for now
    const resp = yield call(
      fetch,
      `https://dotconnect.dotandkey.com/api/v1/department/${deptId}/designations`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
      }
    );

    if (!resp.ok) throw new Error(`Status ${resp.status}`);
    const data = yield resp.json();

    // ✅ correct extraction
    const desgs = data?.data?.designations || [];

    yield put(getDesignationsSuccess(desgs));
  } catch (error) {
    console.error("Designation fetch error:", error);
    yield put(getDesignationsFailure("Failed to load designations"));
  }
}

// ✅ GET USER DETAILS
export function* getUserDetailsSaga() {
  try {
    const token = localStorage.getItem("access_token");

    const response = yield call(
      axios.get,
      "https://dotconnect.dotandkey.com/api/v1/auth/getUserDetails",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    yield put(getUserDetailsSuccess(response.data));
  } catch (error) {
    yield put(
      getUserDetailsFailure(
        error.response?.data || { message: "Something went wrong" }
      )
    );
  }
}

// ✅ LOGOUT (API + cleanup)
export function* logoutSaga(action) {
  try {
    const { token, user_id, onSuccess } = action.payload;

    const response = yield call(
      axios.post,
      "https://dotconnect.dotandkey.com/api/v1/auth/logout",
      { user_id },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("Logout API response 👉", response.data); // 👈 log here

    // clear localStorage + reset slices
    localStorage.removeItem("access_token");
    localStorage.removeItem("user_id");

    yield put(resetArtworkShare());
    yield put(resetArtwork());
    yield put(resetArtworkView());
    yield put(logoutSuccess());

    if (onSuccess) onSuccess(); // trigger redirect/cleanup
  } catch (error) {
    console.error("Logout API failed ❌", error?.response?.data || error.message);
    yield put(
      logoutFailure(
        error?.response?.data || { message: "Logout failed" }
      )
    );
  }
}

// ✅ WATCHER
const watchFunction = [
  (function* () {
    yield takeLatest(loginRequest.type, loginSaga);
    yield takeLatest(signupRequest.type, signupSaga);
    yield takeLatest(getDesignationsRequest.type, getDesignationsSaga);
    yield takeLatest(getUserDetailsRequest.type, getUserDetailsSaga);
    yield takeLatest(logoutRequest.type, logoutSaga); // ✅ real logout with API
  })(),
];

export default watchFunction;
